import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiX, FiCopy, FiShare2, FiCheck } from 'react-icons/fi';
import { setLinkSharingEnabled } from '../services/roundService';
import { trackEvent } from '../services/analytics';
import { guestRoundUrl } from '../utils/host';

// Share a round's public guest link (e.g. https://teesync.co.uk/r/{short_code}).
// Anyone with the link can RSVP without an account; the toggle below is the
// kill switch (link_sharing_enabled). The link origin is environment-aware
// (prod / staging / localhost) via guestRoundUrl().

const ShareRoundModal = ({ isOpen, onClose, round, organiserName, onToggled }) => {
  const [copied, setCopied] = useState(false);
  const [toggling, setToggling] = useState(false);

  if (!round) return null;

  const sharingEnabled = round.link_sharing_enabled !== false;
  const link = guestRoundUrl(round.short_code);
  const message = `${organiserName || 'Someone'} is getting a round together at ${round.course || 'the course'} - tap to say when you can play: ${link}`;

  const trackShared = (method) =>
    trackEvent('guest_link_shared', { short_code: round.short_code, method });

  const handleNativeShare = async () => {
    try {
      await navigator.share({ text: message });
      trackShared('share_sheet');
    } catch {
      // user dismissed the sheet - not an error
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      trackShared('copy');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable - leave button state unchanged
    }
  };

  const handleToggle = async () => {
    setToggling(true);
    const { error } = await setLinkSharingEnabled(round.id, !sharingEnabled);
    setToggling(false);
    if (!error) onToggled?.(!sharingEnabled);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full sm:max-w-md bg-card rounded-t-2xl sm:rounded-2xl border border-border-subtle p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-text-primary">Share round</h2>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="w-9 h-9 rounded-lg bg-surface flex items-center justify-center"
              >
                <SafeIcon icon={FiX} className="w-4 h-4 text-text-secondary" />
              </button>
            </div>

            <p className="text-sm text-text-secondary mb-4">
              Anyone with this link can say when they can play - no account or app needed.
            </p>

            <div className="rounded-lg bg-subtle border border-border-subtle px-3 py-2.5 mb-4 font-mono text-sm text-text-primary break-all">
              {link}
            </div>

            <div className="space-y-2">
              {typeof navigator !== 'undefined' && typeof navigator.share === 'function' && (
                <button
                  type="button"
                  onClick={handleNativeShare}
                  disabled={!sharingEnabled}
                  className="w-full flex items-center justify-center gap-2 rounded-lg bg-accent px-4 py-3 font-semibold text-white disabled:opacity-50"
                >
                  <SafeIcon icon={FiShare2} className="w-4 h-4" />
                  Share
                </button>
              )}
              <button
                type="button"
                onClick={handleCopy}
                disabled={!sharingEnabled}
                className="w-full flex items-center justify-center gap-2 rounded-lg border border-border-subtle bg-surface px-4 py-3 font-semibold text-text-primary disabled:opacity-50"
              >
                <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-4 h-4" />
                {copied ? 'Copied' : 'Copy invite message'}
              </button>
            </div>

            <div className="mt-5 flex items-center justify-between rounded-lg bg-subtle px-3 py-3">
              <div className="mr-3">
                <p className="text-sm font-medium text-text-primary">Link sharing</p>
                <p className="text-xs text-text-secondary">
                  {sharingEnabled
                    ? 'The link is live - turn off to stop new responses.'
                    : 'The link is off - nobody can view or respond.'}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={sharingEnabled}
                disabled={toggling}
                onClick={handleToggle}
                className={`relative h-7 w-12 flex-shrink-0 rounded-full transition-colors ${
                  sharingEnabled ? 'bg-accent' : 'bg-border-subtle'
                }`}
              >
                <span
                  className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${
                    sharingEnabled ? 'left-6' : 'left-1'
                  }`}
                />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ShareRoundModal;
